import React, { useId } from 'react';

import { tv } from '../../tv';

/**
 * The agent's glyph: three sparkles filled with the AI signature gradient.
 * It is the one glyph in the system that is not a currentColor outline
 * (icon.md), because the gradient IS the signature; `fill="current"` is for
 * the seats where it sits on the signature already (a solid AI chip).
 * Breathing is the agent thinking: a slow opacity loop, never a spinner.
 */
const aiIconStyles = tv({
  base: 'ck:shrink-0',
  variants: {
    size: {
      sm: 'ck:size-3',
      md: 'ck:size-4',
      lg: 'ck:size-5',
      xl: 'ck:size-6',
    },
    breathing: {
      true: 'ck:animate-breath',
      false: '',
    },
  },
  defaultVariants: { size: 'md', breathing: false },
});

export interface AiIconProps {
  /** 12, 16, 20 or 24px: the Icon steps, so it sits beside any glyph. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** The signature gradient, or currentColor where the glyph sits on it. */
  fill?: 'gradient' | 'current';
  /** The agent is working: a slow breath, calm on purpose. */
  isBreathing?: boolean;
  /** Names the glyph as an image; without it the icon is decorative. */
  label?: string;
}

const SPARKLES = [
  'M9 4.5C9.33486 4.5 9.62915 4.72198 9.72114 5.04396L10.5343 7.89015C10.8903 9.13593 11.8641 10.1097 13.1099 10.4657L15.956 11.2789C16.278 11.3709 16.5 11.6651 16.5 12C16.5 12.3349 16.278 12.6291 15.956 12.7211L13.1098 13.5343C11.8641 13.8903 10.8903 14.8641 10.5343 16.1099L9.72114 18.956C9.62915 19.278 9.33486 19.5 9 19.5C8.66514 19.5 8.37085 19.278 8.27886 18.956L7.46566 16.1099C7.10972 14.8641 6.13593 13.8903 4.89015 13.5343L2.04396 12.7211C1.72198 12.6291 1.5 12.3349 1.5 12C1.5 11.6651 1.72198 11.3709 2.04396 11.2789L4.89015 10.4657C6.13593 10.1097 7.10972 9.13593 7.46566 7.89015L8.27886 5.04396C8.37085 4.72198 8.66514 4.5 9 4.5Z',
  'M18 1.5C18.3442 1.5 18.6441 1.73422 18.7276 2.0681L18.9865 3.10356C19.2216 4.04406 19.9559 4.7784 20.8964 5.01353L21.9319 5.27239C22.2658 5.35586 22.5 5.65585 22.5 6C22.5 6.34415 22.2658 6.64414 21.9319 6.72761L20.8964 6.98647C19.9559 7.2216 19.2216 7.95594 18.9865 8.89644L18.7276 9.9319C18.6441 10.2658 18.3442 10.5 18 10.5C17.6558 10.5 17.3559 10.2658 17.2724 9.9319L17.0135 8.89644C16.7784 7.95594 16.0441 7.2216 15.1036 6.98647L14.0681 6.72761C13.7342 6.64414 13.5 6.34415 13.5 6C13.5 5.65585 13.7342 5.35586 14.0681 5.27239L15.1036 5.01353C16.0441 4.7784 16.7784 4.04406 17.0135 3.10356L17.2724 2.0681C17.3559 1.73422 17.6558 1.5 18 1.5Z',
  'M16.5 15C16.8228 15 17.1094 15.2066 17.2115 15.5128L17.6058 16.6956C17.7551 17.1435 18.1065 17.4949 18.5544 17.6442L19.7372 18.0385C20.0434 18.1406 20.25 18.4272 20.25 18.75C20.25 19.0728 20.0434 19.3594 19.7372 19.4615L18.5544 19.8558C18.1065 20.0051 17.7551 20.3565 17.6058 20.8044L17.2115 21.9872C17.1094 22.2934 16.8228 22.5 16.5 22.5C16.1772 22.5 15.8906 22.2934 15.7885 21.9872L15.3942 20.8044C15.2449 20.3565 14.8935 20.0051 14.4456 19.8558L13.2628 19.4615C12.9566 19.3594 12.75 19.0728 12.75 18.75C12.75 18.4272 12.9566 18.1406 13.2628 18.0385L14.4456 17.6442C14.8935 17.4949 15.2449 17.1435 15.3942 16.6956L15.7885 15.5128C15.8906 15.2066 16.1772 15 16.5 15Z',
];

/* The drawing both forms share: the signature gradient (per-instance ids, so
 * two glyphs on one page never share a def) and the three sparkles. */
function Sparkles({ fill }: { fill: 'gradient' | 'current' }) {
  const gradientId = `ck-ai-${useId().replace(/:/g, '')}`;
  const paint = fill === 'gradient' ? `url(#${gradientId})` : 'currentColor';
  return (
    <>
      {fill === 'gradient' && (
        <defs>
          <linearGradient
            id={gradientId}
            x1="2"
            y1="4"
            x2="22"
            y2="22"
            gradientUnits="userSpaceOnUse"
          >
            <stop style={{ stopColor: 'var(--ck-ai-gradient-stop-start)' }} />
            <stop offset="0.55" style={{ stopColor: 'var(--ck-ai-gradient-stop-mid)' }} />
            <stop offset="1" style={{ stopColor: 'var(--ck-ai-gradient-stop-end)' }} />
          </linearGradient>
        </defs>
      )}
      {SPARKLES.map((d) => (
        <path key={d.slice(0, 12)} fillRule="evenodd" clipRule="evenodd" d={d} fill={paint} />
      ))}
    </>
  );
}

export const AiIcon = React.forwardRef<SVGSVGElement, AiIconProps>(function AiIcon(
  { size = 'md', fill = 'gradient', isBreathing = false, label },
  ref,
) {
  return (
    <svg
      ref={ref}
      viewBox="0 0 24 24"
      fill="none"
      className={aiIconStyles({ size, breathing: isBreathing })}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
      data-component="ai-icon"
    >
      <Sparkles fill={fill} />
    </svg>
  );
});

/**
 * The same glyph shaped as a control's glyph: Button's `iconStart` and
 * IconButton's `icon` take it, and the control sizes it from its own recipe,
 * as it does any glyph (icon.md). An action that asks the agent carries this,
 * never a generic sparkles outline (ai.md). Always the gradient: on the solid
 * AI fill the fill is already the signature, so that seat takes no glyph.
 */
export const AiGlyph = React.forwardRef<SVGSVGElement, React.SVGProps<SVGSVGElement>>(
  function AiGlyph(props, ref) {
    return (
      <svg {...props} ref={ref} viewBox="0 0 24 24" fill="none" data-component="ai-glyph">
        <Sparkles fill="gradient" />
      </svg>
    );
  },
);
